import type { IntlShape } from 'react-intl'

import type { Icon } from '@slite/icons'
import {
  ClockIcon,
  CloseIcon,
  GlobeIcon,
  LockIcon,
  PenIcon,
} from '@slite/icons'

import { createRoutes } from '../../routes'

const readAndWriteAccessIllustration =
  'https://cdn.sanity.io/images/dnsgqii5/production/f3d14962ecd23b971ad164d918b711a6d7f3e442-1200x1000.png'
const offloadSearchIllustration =
  'https://cdn.sanity.io/images/dnsgqii5/production/4b2dc39181ebfc8305755dd8714e255e796bd740-1176x602.png'
const guardrailsIllustration =
  'https://cdn.sanity.io/images/dnsgqii5/production/1d9c43b21ee7128f0f846225529ddb44a8fbad23-1176x602.png'

type ComparisonBar = {
  label: string
  sublabel: string
  value: number
  /** Fill width normalized to the chart's largest value */
  fillPct: number
  theme: 'brand' | 'neutral'
}

export type ComparisonChart = {
  title: string
  subtitle: string
  bars: ComparisonBar[]
  valueSuffix: string
}

export function getMcpComparisonCharts(intl: IntlShape): ComparisonChart[] {
  return [
    {
      title: intl.formatMessage({
        id: 'mcp.comparison.accuracy.title',
        defaultMessage: 'Accuracy',
      }),
      subtitle: intl.formatMessage({
        id: 'mcp.comparison.accuracy.subtitle',
        defaultMessage:
          '41 real company questions. % of answers rated correct or better.',
      }),
      valueSuffix: '%',
      bars: [
        {
          label: 'Super',
          sublabel: 'App',
          value: 90,
          fillPct: 100,
          theme: 'brand',
        },
        {
          label: 'Claude + Super',
          sublabel: 'MCP',
          value: 80,
          fillPct: (80 / 90) * 100,
          theme: 'brand',
        },
        {
          label: 'Claude + 8 MCPs',
          sublabel: 'Multiple connectors',
          value: 68,
          fillPct: (68 / 90) * 100,
          theme: 'neutral',
        },
      ],
    },
    {
      title: intl.formatMessage({
        id: 'mcp.comparison.speed.title',
        defaultMessage: 'Speed',
      }),
      subtitle: intl.formatMessage({
        id: 'mcp.comparison.speed.subtitle',
        defaultMessage: 'Average response time. Lower is better.',
      }),
      valueSuffix: 's',
      bars: [
        {
          label: 'Super',
          sublabel: 'App',
          value: 39.5,
          fillPct: (39.5 / 101.9) * 100,
          theme: 'brand',
        },
        {
          label: 'Claude + Super',
          sublabel: 'MCP',
          value: 76.8,
          fillPct: (76.8 / 101.9) * 100,
          theme: 'brand',
        },
        {
          label: 'Claude + 8 MCPs',
          sublabel: 'Multiple connectors',
          value: 101.9,
          fillPct: 100,
          theme: 'neutral',
        },
      ],
    },
  ]
}

export type McpVideo = {
  // Stable React key — youtubeId is not unique while placeholder ids are in
  key: string
  youtubeId: string
  caption: string
}

export function getMcpVideos(intl: IntlShape): McpVideo[] {
  return [
    {
      key: 'generate',
      youtubeId: '0N69TdQOnvo',
      caption: intl.formatMessage({
        id: 'mcp.videos.video1.caption',
        defaultMessage: 'Generate & Document on the Fly',
      }),
    },
    {
      key: 'comments',
      youtubeId: '0N69TdQOnvo',
      caption: intl.formatMessage({
        id: 'mcp.videos.video2.caption',
        defaultMessage: 'Ask Claude to check all comments across 20 docs',
      }),
    },
    {
      key: 'bulk-update',
      youtubeId: '0N69TdQOnvo',
      caption: intl.formatMessage({
        id: 'mcp.videos.video3.caption',
        defaultMessage: 'Update docs in bulk, right from Cursor',
      }),
    },
  ]
}

export function getMcpFAQ(intl: IntlShape) {
  const routes = createRoutes(intl.locale)
  return [
    {
      question: intl.formatMessage({
        id: 'mcp.faq.question1',
        defaultMessage: 'What is the Slite MCP server?',
      }),
      answer: intl.formatMessage({
        id: 'mcp.faq.answer1',
        defaultMessage:
          'MCP (Model Context Protocol) is an open standard that lets AI tools securely connect to other systems. The Slite MCP server gives any MCP-compatible AI access to your company knowledge base: it can search across your connected sources with Super, and read, write, create and organize your Slite docs — all with the permissions you already have.',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'mcp.faq.question2',
        defaultMessage: 'Which AI tools does it work with?',
      }),
      answer: intl.formatMessage({
        id: 'mcp.faq.answer2',
        defaultMessage:
          'It works out-of-the-box with any MCP-compatible client, including Claude Desktop, Cursor, Windsurf, and custom agent frameworks like LangChain or LlamaIndex.',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'mcp.faq.question3',
        defaultMessage:
          'Why not just connect each of my sources with its own MCP?',
      }),
      answer: intl.formatMessage({
        id: 'mcp.faq.answer3',
        defaultMessage:
          'Two reasons: Latency and Context. If you connect 8 separate MCPs, your LLM has to decide which tool to call, run them sequentially, and manage massive token overhead. Slite MCP acts as a single, highly-optimized gateway that searches all sources in parallel and returns a single, synthesized, high-relevance context package.',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'mcp.faq.question4',
        defaultMessage: 'Is the Slite MCP server a replacement for RAG?',
      }),
      answer: intl.formatMessage({
        id: 'mcp.faq.answer4',
        defaultMessage:
          'No — they solve different problems. RAG retrieves chunks from a vector index you have to build, sync, and refresh; the Slite MCP server gives your agent live, permissioned access to the source itself, and lets it write back. Use RAG when you are building a custom pipeline over a corpus you control. Use the Slite MCP server when your agent should search up-to-date company knowledge and act on it — with no index to maintain.',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'mcp.faq.question5',
        defaultMessage: 'Is the Slite MCP server secure?',
      }),
      answer: intl.formatMessage(
        {
          id: 'mcp.faq.answer5',
          defaultMessage:
            'Yes. The Slite MCP server authenticates over OAuth and inherits the exact permissions of the signed-in user, so an agent can only reach docs that person could already open. Slite is SOC 2 Type II certified and GDPR-compliant, with SSO (Google, Okta, Azure AD), 2FA, and encryption in transit and at rest with EU data residency. HIPAA support is available for Enterprise customers. Your data is never used to train public models, and any write action can be queued for human approval. Full details in our <link>Privacy & Security documentation</link>.',
        },
        {
          link: (chunks) => (
            <a
              href={routes.legal.security()}
              className="underline saturn-orange"
            >
              {chunks}
            </a>
          ),
        }
      ),
    },
    {
      question: intl.formatMessage({
        id: 'mcp.faq.question6',
        defaultMessage: 'How do I set up the Slite MCP server?',
      }),
      answer: intl.formatMessage(
        {
          id: 'mcp.faq.answer6',
          defaultMessage:
            'Add https://api.slite.com/mcp as a remote MCP server in your AI client and authorize with OAuth. In Claude Desktop: Settings → Connectors → Add custom connector. ChatGPT, Cursor, Gemini, and Codex follow the same remote-URL pattern. There is no install and no config file to edit. Step-by-step setup for each client is in our <link>Slite MCP guide</link>.',
        },
        {
          link: (chunks) => (
            <a
              href={routes.external.mcpHelp.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline saturn-orange"
            >
              {chunks}
            </a>
          ),
        }
      ),
    },
  ]
}

export const MCP_TOOLS: { label: string; Icon: Icon }[] = [
  { label: 'search-note', Icon: GlobeIcon },
  { label: 'get-note', Icon: GlobeIcon },
  { label: 'create-note', Icon: PenIcon },
  { label: 'get-comment-thread', Icon: GlobeIcon },
  { label: 'update-note', Icon: PenIcon },
  { label: 'create-collection', Icon: PenIcon },
  { label: 'update-collection', Icon: PenIcon },
  { label: 'modify-range', Icon: PenIcon },
  { label: 'remove-blocks', Icon: CloseIcon },
  { label: 'move-note', Icon: PenIcon },
  { label: 'archive-note', Icon: CloseIcon },
  { label: 'restore-note', Icon: GlobeIcon },
]

export function getMcpWhySuperContent(intl: IntlShape) {
  return {
    title: intl.formatMessage({
      id: 'mcp.whysuper.title',
      defaultMessage:
        'What makes Slite the perfect context layer for your AI agents?',
    }),
    description: intl.formatMessage({
      id: 'mcp.whysuper.subtitle',
      defaultMessage:
        'Most MCPs are read-only windows into messy folders. Slite MCP gives your AI agent surgical read-and-write powers.',
    }),
  }
}

export type McpWhySuperCard = {
  title: string
  description: string
  image: {
    src: string
    alt: string
    objectPosition: 'top left' | 'top right'
  }
}

export function getMcpWhySuperCards(intl: IntlShape): McpWhySuperCard[] {
  return [
    {
      title: intl.formatMessage({
        id: 'mcp.whysuper.bento1.title',
        defaultMessage: 'Complete Read & Write Access',
      }),
      description: intl.formatMessage({
        id: 'mcp.whysuper.bento1.description',
        defaultMessage:
          'Equip your agent with 40+ built-in tools to search sources, manipulate documents, manage structured tables, and organize your knowledge base.',
      }),
      image: {
        src: readAndWriteAccessIllustration,
        alt: intl.formatMessage({
          id: 'mcp.whysuper.bento1.illustrationAlt',
          defaultMessage: 'Complete Read & Write Access',
        }),
        objectPosition: 'top left',
      },
    },
    {
      title: intl.formatMessage({
        id: 'mcp.whysuper.bento2.title',
        defaultMessage: 'Offload the search to Slite',
      }),
      description: intl.formatMessage({
        id: 'mcp.whysuper.bento2.description',
        defaultMessage:
          'Relying on scattered MCPs is painfully slow and wastes context. Let our agent handle search and edits in one go.',
      }),
      image: {
        src: offloadSearchIllustration,
        alt: intl.formatMessage({
          id: 'mcp.whysuper.bento2.illustrationAlt',
          defaultMessage: 'Offload the search to Slite',
        }),
        objectPosition: 'top right',
      },
    },
    {
      title: intl.formatMessage({
        id: 'mcp.whysuper.bento3.title',
        defaultMessage: 'Human-in-the-loop approvals',
      }),
      description: intl.formatMessage({
        id: 'mcp.whysuper.bento3.description',
        defaultMessage:
          "Run autonomous agents safely. Slite's protocol can queue actions for human approval.",
      }),
      image: {
        src: guardrailsIllustration,
        alt: intl.formatMessage({
          id: 'mcp.whysuper.bento3.illustrationAlt',
          defaultMessage: 'Guardrails when needed',
        }),
        objectPosition: 'top left',
      },
    },
  ]
}

export type McpWhySuperHighlight = {
  title: string
  description: string
  Icon: Icon
}

export function getMcpWhySuperHighlights(
  intl: IntlShape
): McpWhySuperHighlight[] {
  return [
    {
      title: intl.formatMessage({
        id: 'mcp.whysuper.bento4.title',
        defaultMessage: 'Zero-trust security',
      }),
      description: intl.formatMessage({
        id: 'mcp.whysuper.bento4.description',
        defaultMessage:
          'MCP mirrors the permissions you actually have in the docs or connected sources.',
      }),
      Icon: LockIcon,
    },
    {
      title: intl.formatMessage({
        id: 'mcp.whysuper.bento5.title',
        defaultMessage: 'Parallelized queries',
      }),
      description: intl.formatMessage({
        id: 'mcp.whysuper.bento5.description',
        defaultMessage:
          'Slite handles tasks in parallel, returning context in seconds.',
      }),
      Icon: ClockIcon,
    },
    {
      title: intl.formatMessage({
        id: 'mcp.whysuper.bento6.title',
        defaultMessage: 'Jargon & context-aware',
      }),
      description: intl.formatMessage({
        id: 'mcp.whysuper.bento6.description',
        defaultMessage:
          "Slite maps your company's unique vocabulary, acronyms, and team structures.",
      }),
      Icon: GlobeIcon,
    },
  ]
}
